import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Loader2, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useModalContext } from '../components/ModalProvider';
import { ActivationEvent, PerspectiveScope, activatePhase, deleteSitePhase } from './sitePerspectiveApi';

/**
 * The activation control beside the property selectors.
 *
 * A phase must be activated before its perspectives exist, and until they exist there is nothing to
 * show and nothing for a drill-down to write into. So this is not a convenience — it is the gate,
 * and it says so in the destructive colour.
 *
 * Activation streams: the server reports each step as it finishes, and they appear here in that
 * order. Twelve steps at five seconds each would otherwise be a spinner with nothing behind it.
 *
 * Once activated, the same place offers to delete the phase again — with everything the activation
 * produced. That is the opposite of a convenience too, which is why it asks for the phase's id.
 */

interface Properties {
    scope: PerspectiveScope | null;
    activated: boolean;
    phaseLabel: string;
    onActivated: () => void;
    /** The activated phase, where it has one. Absent for phases activated before phases were rows. */
    sitePhaseId?: string | null;
    onDeleted?: () => void;
}

export default function PhaseActivation({
    scope,
    activated,
    phaseLabel,
    onActivated,
    sitePhaseId,
    onDeleted,
}: Properties) {
    const { t } = useTranslation();
    const { showDeleteDialog, pushError, pushSuccess } = useModalContext() as any;
    const [running, setRunning] = useState(false);
    const [steps, setSteps] = useState<string[]>([]);
    const [error, setError] = useState<string | null>(null);

    // A failure belongs to the phase it happened in. This component is never remounted on a change
    // of property, so without this the message from one site would sit beside the next site's
    // "activated" and accuse it of something it did not do. The key is spelled out rather than
    // stringified, because the scope object is rebuilt on every render of the page.
    const scopeKey = useMemo(
        () =>
            scope
                ? [
                      scope.street,
                      scope.streetNumber,
                      scope.postalCode,
                      scope.city,
                      scope.projectType,
                      scope.phase,
                  ]
                      .map((part) => String(part ?? ''))
                      .join('|')
                : null,
        [scope],
    );

    useEffect(() => {
        setError(null);
        setSteps([]);
    }, [scopeKey]);

    const confirmDelete = () => {
        if (!sitePhaseId) return;

        // The keyword is the phase's own id: typing it is the one thing that cannot be done by
        // reflex, and it is what distinguishes this phase from the identically named one next door.
        showDeleteDialog(
            t('portfolioDashboard.activation.deleteTitle', { phase: phaseLabel }),
            sitePhaseId,
            async () => {
                try {
                    const result = await deleteSitePhase(sitePhaseId);
                    pushSuccess(
                        t('portfolioDashboard.activation.deleted', {
                            perspectives: result.perspectives,
                            checklists: result.checklists,
                        }),
                    );
                    await onDeleted?.();
                } catch (deleteError: any) {
                    console.error('Could not delete the phase', deleteError);
                    pushError(deleteError);
                }
            },
        );
    };

    if (activated && !running) {
        return (
            <span className="inline-flex flex-wrap items-center gap-2">
                <span className="whitespace-nowrap text-sm font-bold text-destructive">
                    {t('portfolioDashboard.activation.done', { phase: phaseLabel })}
                </span>
                {sitePhaseId ? (
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                        title={t('portfolioDashboard.activation.delete', { phase: phaseLabel })}
                        aria-label={t('portfolioDashboard.activation.delete', { phase: phaseLabel })}
                        onClick={confirmDelete}
                    >
                        <Trash2 size={14} strokeWidth={2} />
                    </Button>
                ) : (
                    <></>
                )}
                {/* A step can fail while the perspectives are created, and then this branch replaces
                    the one that showed the message. The phase *is* activated and something *did*
                    go wrong; both belong on screen. */}
                {error ? <span className="text-sm text-destructive">{error}</span> : <></>}
            </span>
        );
    }

    if (!scope) {
        return <></>;
    }

    const start = async () => {
        setRunning(true);
        setSteps([]);
        setError(null);

        try {
            await activatePhase(scope, (event: ActivationEvent) => {
                if (event.type === 'step') {
                    setSteps((current) => [...current, event.label]);
                } else if (event.type === 'error') {
                    setError(event.message);
                }
            });
            // Reload either way. A failed step does not undo the ones that worked — the
            // perspectives may well exist now — and the screen should show what is actually there.
            // The message stays; the row below carries it next to the activated marker.
            await onActivated();
        } catch (activationError: any) {
            console.error('Could not activate the phase', activationError);
            setError(activationError?.message ?? t('global.generalError'));
        } finally {
            setRunning(false);
        }
    };

    return (
        <div className="flex flex-col gap-1.5">
            <Button
                type="button"
                variant="outline"
                size="sm"
                className="border-destructive font-bold text-destructive hover:bg-destructive/10 hover:text-destructive"
                disabled={running}
                onClick={start}
            >
                {running ? <Loader2 size={14} strokeWidth={2} className="mr-2 animate-spin" aria-hidden /> : <></>}
                {running
                    ? t('portfolioDashboard.activation.running')
                    : t('portfolioDashboard.activation.activate')}
            </Button>

            {steps.length > 0 ? (
                <div className="flex max-h-[120px] flex-col gap-0.5 overflow-y-auto">
                    {steps.map((step, index) => (
                        <div
                            key={`${step}-${index}`}
                            className="flex items-center gap-1.5 text-xs text-muted-foreground"
                        >
                            <CheckCircle2 size={12} strokeWidth={2} className="shrink-0" />
                            {step}
                        </div>
                    ))}
                </div>
            ) : (
                <></>
            )}

            {error ? <span className="text-sm text-destructive">{error}</span> : <></>}
        </div>
    );
}
